import React, { useState, useEffect } from "react";
import { XMarkIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { OrganizationOKR, okrService } from "../../services/okrs";
import {
  OKRRelationshipCreate,
  okrRelationshipsService,
} from "../../services/okrRelationships";

interface AddRelationshipModalProps {
  open: boolean;
  onClose: () => void;
  okrId: string;
  organizationId: string;
  onRelationshipAdded: () => void;
  currentOkrObjective: string;
}

export const AddRelationshipModal: React.FC<AddRelationshipModalProps> = ({
  open,
  onClose,
  okrId,
  organizationId,
  onRelationshipAdded,
  currentOkrObjective,
}) => {
  const [relationshipType, setRelationshipType] = useState<
    "contributes_to" | "depends_on" | "aligned_with"
  >("contributes_to");
  const [targetType, setTargetType] = useState<"okr">("okr"); // For now, only OKR to OKR
  const [searchQuery, setSearchQuery] = useState("");
  const [availableOkrs, setAvailableOkrs] = useState<OrganizationOKR[]>([]);
  const [filteredOkrs, setFilteredOkrs] = useState<OrganizationOKR[]>([]);
  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null);
  const [contributionNotes, setContributionNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load available OKRs when modal opens
  useEffect(() => {
    if (open) {
      loadAvailableOkrs();
    } else {
      // Reset form when modal closes
      setRelationshipType("contributes_to");
      setSearchQuery("");
      setSelectedTargetId(null);
      setContributionNotes("");
    }
  }, [open]);

  // Filter OKRs based on search query
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredOkrs(availableOkrs);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = availableOkrs.filter(
        (okr) =>
          okr.objective.toLowerCase().includes(query) ||
          (okr.description && okr.description.toLowerCase().includes(query)),
      );
      setFilteredOkrs(filtered);
    }
  }, [searchQuery, availableOkrs]);

  const loadAvailableOkrs = async () => {
    setLoading(true);
    try {
      const okrs = await okrRelationshipsService.getPotentialTargets(
        organizationId,
        okrId,
      );
      setAvailableOkrs(okrs);
      setFilteredOkrs(okrs);
    } catch (error) {
      console.error("Failed to load available OKRs:", error);
      alert("Failed to load available OKRs");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedTargetId) {
      alert("Please select a target OKR");
      return;
    }

    setSaving(true);
    try {
      const relationship: OKRRelationshipCreate = {
        target_type: targetType,
        target_id: selectedTargetId,
        relationship_type: relationshipType,
        contribution_notes: contributionNotes.trim() || undefined,
      };

      await okrRelationshipsService.createRelationship(
        okrId,
        relationship,
        organizationId,
      );
      onRelationshipAdded();
      onClose();
    } catch (error: any) {
      console.error("Failed to create relationship:", error);
      alert(error.response?.data?.detail || "Failed to create relationship");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const relationshipDescriptions = {
    contributes_to: "This OKR helps achieve the selected OKR",
    depends_on: "This OKR requires the selected OKR to be successful",
    aligned_with: "This OKR works toward similar goals as the selected OKR",
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div
          className="fixed inset-0 bg-gray-500/75"
          onClick={onClose}
        />
        <div className="relative bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Add Cross-Functional Relationship
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Connect &quot;{currentOkrObjective}&quot; with another OKR
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Relationship Type */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Relationship Type
            </label>
            <div className="space-y-2">
              {(["contributes_to", "depends_on", "aligned_with"] as const).map(
                (type) => (
                  <label
                    key={type}
                    className="flex items-start cursor-pointer p-3 border rounded-lg hover:bg-gray-50"
                  >
                    <input
                      type="radio"
                      value={type}
                      checked={relationshipType === type}
                      onChange={(e) =>
                        setRelationshipType(
                          e.target.value as typeof relationshipType,
                        )
                      }
                      className="mt-1 mr-3"
                    />
                    <div>
                      <div className="font-medium text-gray-900">
                        {type
                          .replace("_", " ")
                          .replace(/\b\w/g, (l) => l.toUpperCase())}
                      </div>
                      <div className="text-sm text-gray-500">
                        {relationshipDescriptions[type]}
                      </div>
                    </div>
                  </label>
                ),
              )}
            </div>
          </div>

          {/* Search and Select Target OKR */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Target OKR
            </label>

            {/* Search Bar */}
            <div className="relative mb-3">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search objectives..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>

            {/* OKR List */}
            <div className="border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center text-gray-500">Loading...</div>
              ) : filteredOkrs.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  {searchQuery ? "No matching OKRs found" : "No available OKRs"}
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredOkrs.map((okr) => (
                    <label
                      key={okr.id}
                      className="flex items-start p-3 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="radio"
                        value={okr.id}
                        checked={selectedTargetId === okr.id}
                        onChange={(e) => setSelectedTargetId(e.target.value)}
                        className="mt-1 mr-3"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">
                          {okr.formatted_reference &&
                            `${okr.formatted_reference} - `}
                          {okr.objective}
                        </p>
                        {okr.description && (
                          <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                            {okr.description}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          {okr.is_corporate && (
                            <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                              Corporate
                            </span>
                          )}
                          {okr.owner && (
                            <span className="text-xs text-gray-500">
                              Owner:{" "}
                              {okr.owner.first_name || okr.owner.last_name
                                ? `${okr.owner.first_name || ""} ${okr.owner.last_name || ""}`.trim()
                                : okr.owner.email}
                            </span>
                          )}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Notes (Optional) */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (Optional)
            </label>
            <textarea
              value={contributionNotes}
              onChange={(e) => setContributionNotes(e.target.value)}
              placeholder="Describe how these OKRs are related..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!selectedTargetId || saving}
              className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? "Creating..." : "Create Relationship"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
